/**
 * What a refused request means, decided in one place — and the one refusal that ends a session
 * without asking the authority (`PLT-014` AC-7, `ADR 051` §6).
 *
 * ## The gap this closes
 *
 * `session-recheck.ts` turned every 401 and 403 into one question — *is the session alive?* —
 * and `/auth/me` answered it. That is right for an expired token and right for a decayed
 * organizer membership, and it has one blind spot: a **suspended account whose token is still
 * valid**. Its every request is refused with 403; `/auth/me` looks at the token and says *alive*;
 * the console concludes, correctly on that evidence, that each refusal was about permission and
 * says so once (`PLT-014` AC-3). The person is left on a screen of refusals with no explanation
 * and nothing to sign out of — indistinguishable, at the client, from lacking one capability.
 *
 * The status cannot tell the two apart. The API's `errorCode` can, and now travels with the
 * status (`@h2hspark/ui`'s `RefusedRequest`), so this is where the branch lives: a refusal
 * carrying {@link ACCOUNT_SUSPENDED_ERROR_CODE} ends the session **on that code alone**; every
 * other refusal is the doubt it always was and goes to `/auth/me` as before. A `PERMISSION_DENIED`
 * still signs nobody out.
 *
 * ## Why this is the second signal and not a flag on the first
 *
 * A re-check is a *question*. This is an *answer*. Routing the answer through the question would
 * mean the provider asking `/auth/me` about a session it has just been told is over, and — since
 * the token is valid — being told it is alive, and restoring the user it was clearing. The two
 * cannot share a path, so they do not share a function.
 *
 * Same shape as the re-check otherwise, for the same reasons that file gives: module-level,
 * because the transport is built at module load with no provider to hand it; latched, because a
 * refusal that arrives before the provider subscribes must not be dropped. Latching **the
 * reason** rather than a bit: the reason is what the sign-in screen renders.
 */
import { requestSessionRecheck } from './session-recheck.js';
/**
 * The code the API sends when the account behind a valid token is suspended.
 *
 * Restated from `@h2hspark/errors`' `ACCOUNT_SUSPENDED_ERROR_CODE`, which this browser package
 * cannot import; `__tests__/a-suspended-account-ends-its-session.test.ts` holds the two spellings
 * together the way `a-restated-type-still-has-to-match.test.ts` holds the session shape.
 */
export const ACCOUNT_SUSPENDED_ERROR_CODE = 'ACCOUNT_SUSPENDED';
/**
 * The sentence the sign-in screen shows for each reason.
 *
 * Here rather than in three consoles, so the same account is told the same thing whichever
 * console it was refused on. It says what happened and who can undo it, and nothing about why:
 * the reason for a suspension is an operator's, and the roster does not send it.
 */
const SESSION_END_SENTENCES = {
    'account-suspended': 'Your account has been suspended, so you have been signed out. Contact H2HSpark to have it reinstated.',
};
export function describeSessionEnd(reason) {
    return SESSION_END_SENTENCES[reason];
}
const listeners = new Set();
/** A reason that arrived before anybody was listening — see `session-recheck.ts` for the race. */
let reasonBeforeAnybodyListened = null;
/**
 * Subscribe to session-ending signals. Returns an unsubscribe function.
 *
 * Called by `AuthProvider`; there is no reason for an application to call it.
 */
export function onSessionEndRequested(listener) {
    listeners.add(listener);
    if (reasonBeforeAnybodyListened !== null) {
        const held = reasonBeforeAnybodyListened;
        reasonBeforeAnybodyListened = null;
        listener(held);
    }
    return () => {
        listeners.delete(listener);
    };
}
/**
 * End the session now, for a stated reason.
 *
 * Safe to call from anywhere and before any provider has mounted: with no listener the reason is
 * held, not dropped, and delivered to the first subscriber.
 */
export function endSessionBecause(reason) {
    if (listeners.size === 0) {
        reasonBeforeAnybodyListened = reason;
        return;
    }
    for (const listener of listeners)
        listener(reason);
}
/**
 * **What a console hands the transport as `onUnauthenticated`.**
 *
 * One branch, on the code and never on the status: `ACCOUNT_SUSPENDED` ends the session; anything
 * else — `PERMISSION_DENIED`, `AUTHENTICATION_REQUIRED`, a body with no code at all — is a reason
 * to ask `/auth/me`, exactly as before. The status is not consulted because the code is the
 * server's own statement of what it refused and the status is only its category; a suspension
 * that arrived as some other 4xx would still be a suspension.
 */
export function reportRefusal(refusal) {
    if (refusal.errorCode === ACCOUNT_SUSPENDED_ERROR_CODE) {
        endSessionBecause('account-suspended');
        return;
    }
    requestSessionRecheck();
}
/** Drops the held reason. **Tests only** — the registry is module-level. */
export function resetSessionEndForTests() {
    listeners.clear();
    reasonBeforeAnybodyListened = null;
}
//# sourceMappingURL=session-end.js.map